import { isInLightspeed, postMessageToLightspeed } from '@/scaffolding/tko-lightspeed';
import tkoModal from '@/scaffolding/tko-modal';

/**
 * The Vue name of the add or edit project wizard. TKO gets here through
 * `app.modal.Show('addOrEditProjectWizard', ...)`, which `tko/src/app/helpers/modal.coffee` maps to
 * this name and dispatches to this action, so this action is the one intercept point of both apps.
 */
const PROJECT_WIZARD_MODAL_NAME = 'add-edit-project-wizard-modal';

/**
 * The params of the Lightspeed wizard (`projectAddOrEditDialogProps.ts`). Lightspeed parses and
 * corrects each value, so the bridge only picks and makes the params cloneable.
 *
 * `postMessage` carries structured-cloneable data only, and `preselectedTemplate` is a TKO project
 * model or a REST template, not the V3 template that the wizard applies. The bridge therefore sends
 * the id and the code of the template, and the wizard loads the template itself.
 *
 * `context`, `callback`, `onCloseCallBack`, `clickOutsideToClose` and `escToClose` do not go over:
 * the bridge cannot carry a function, and the close rules come from the dirty state of the form.
 */
function toProjectWizardDialogProps(props = {}) {
  const { preselectedTemplate, preselectedCompany } = props;
  return {
    id: props.id,
    type: props.type,
    tab: props.tab,
    source: props.source,
    preselectedTemplateId: preselectedTemplate?.id ?? props.preselectedTemplateId,
    preselectedTemplateCode: preselectedTemplate?.code ?? props.preselectedTemplateCode,
    preselectedCompany: preselectedCompany ? { id: preselectedCompany.id, name: preselectedCompany.name } : undefined,
    redirectToProject: props.redirectToProject,
  };
}

export default {
  namespaced: true,
  modules: {
    ...tkoModal,
  },
  state: {
    modals: [],
  },
  getters: {
    active: (state) => state.modals.slice(-1)[0],
  },
  mutations: {
    open(state, { name, props = {} } = {}) {
      state.modals = [...state.modals, { name, props, ref: `${name}-${state.modals.length}` }];
    },
    close(state) {
      // Close the topmost modal
      state.modals.splice(-1, 1);
    },
    reset(state) {
      // Reset modal array to empty
      state.modals = [];
    },
  },
  actions: {
    open({ commit, getters: { active } }, { name, props } = {}) {
      if (!name || (active && active.name === name)) {
        return;
      }
      // The flag comes from `window.app`, because a store action has no injection context.
      if (
        name === PROJECT_WIZARD_MODAL_NAME &&
        isInLightspeed() &&
        window.app?.features?.migrateAddEditProjectWizardEnabled?.()
      ) {
        postMessageToLightspeed('twa:open-dialog', {
          dialogName: 'ProjectAddOrEditDialog',
          dialogProps: toProjectWizardDialogProps(props),
        });
        return;
      }
      commit('open', { name, props });
    },
    close({ commit }) {
      commit('close');
    },
    reset: ({ commit }) => commit('reset'),
  },
};
